import { Injectable } from '@angular/core';
import { environment } from '@env';
import { translate } from '@ngneat/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { forkJoin, from, of } from 'rxjs';
import { catchError, debounceTime, delay, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { ActionUpdate, Comment, Task } from '../../interface/task.interface';
import { ApiService } from '../../services/api/api.service';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import {
    getActionUpdates$,
    getComments$,
    getRootChildTasks$,
    getSelectedTaskId$,
    getSelectedTaskInstances$,
    getSelectedTaskType$,
    getTaskActions$,
    getTaskAssignees$,
    getTaskBySelectedType$,
    getTaskDefinitionId$,
    getTasks$,
    getTasksOrganizationMap$,
    getTaskUserMetaprincipalIds$,
} from '../selectors/tasks.selector';
import { getAllUsersMap$ } from '../selectors/users.selector';
import { getCurrentOrganization$ } from '../selectors/locations.selector';
import { getCurrentOrganizationId$, getSessionState$ } from '../selectors/session.selector';
import { getSessionUserPermissions$ } from '../selectors/permissions.selector';
import { Organization } from '../../models/organization.class';
import { TaskAssignee } from '../../interface/task_assignee.interface';

import * as actions from '../actions';
import { AlertError, AlertSuccess } from '../actions/shared.actions';

@Injectable()
export class TasksEffects {
    private fetchingTaskDetail: {
        [id: string]: boolean;
    } = {};
    private fetchingTaskAssignees: {
        [id: string]: boolean;
    } = {};

    constructor(private actions$: Actions, private _api: ApiService, private _store: Store) {}

    private updateAssignees = (
        taskType: 'TASK_DEFINITION' | 'TASK_INSTANCE',
        taskId: string,
        update: boolean,
        assigneesChanged: boolean,
        assignees: TaskAssignee[],
        fromTable?: boolean,
        showMessage = true
    ) => {
        if (!assigneesChanged) {
            this._store.dispatch(actions.GetTaskAssignees({ taskId }));
            return;
        }
        if (taskType === 'TASK_DEFINITION') {
            assignees?.length > 0 &&
                this._store.dispatch(
                    actions.UpdateTaskAssignees({
                        taskId,
                        assignees,
                        message: showMessage ? translate(`Task ${update ? 'Updated' : 'Created'} Successfully`) : undefined,
                        taskType: 'TASK_DEFINITION',
                        fromTable,
                    })
                );
        } else if (taskType === 'TASK_INSTANCE') {
            assignees?.length > 0 &&
                this._store.dispatch(
                    actions.UpdateTaskAssignees({
                        taskId,
                        assignees,
                        message: showMessage ? translate(`Task ${update ? 'Updated' : 'Created'} Successfully`) : undefined,
                        taskType: 'TASK_INSTANCE',
                        fromTable,
                    })
                );
        }
    };

    createTask$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.CreateTask),
            debounceTime(100),
            withLatestFrom(
                this._store.select(getSelectedTaskType$),
                this._store.select(getAllUsersMap$),
                this._store.select(getCurrentOrganization$)
            ),
            mergeMap(([action, taskType, usersMap, currentOrganization]) => {
                if (!action.taskChanged) {
                    this.updateAssignees(
                        taskType,
                        action.task.id,
                        action.update,
                        action.assigneesChanged,
                        action.assignees,
                        action.fromTable,
                        true
                    );
                    return [];
                }
                return from(this._api.tasks.createTask({ task: action.task, taskType })).pipe(
                    map((res: Task) => {
                        res.task_url = `${environment['encomply-ui']}/organizations/${currentOrganization.id}/tasks/${res?.id}?taskDefinitionId=${res?.parent}&taskType=${taskType}&rootDefinitionId=${res?.root_definition_id}`;
                        const resTask: Task = this.prepareTasksWithUserName([res], usersMap, currentOrganization)?.[0];
                        this.updateAssignees(
                            taskType,
                            resTask.id,
                            action.update,
                            action.assigneesChanged,
                            action.assignees,
                            action.fromTable,
                            false
                        );
                        switch (taskType) {
                            case 'TASK_DEFINITION':
                                if (action.isRootPlan) {
                                    this._store.dispatch(
                                        actions.AlertSuccess({
                                            message: action.update
                                                ? translate('Plan Updated Successfully')
                                                : translate('Plan Created Successfully'),
                                        })
                                    );
                                } else {
                                    this._store.dispatch(
                                        actions.AlertSuccess({
                                            message: action.update
                                                ? translate('Task Updated Successfully')
                                                : translate('Task Created Successfully'),
                                        })
                                    );
                                }
                                return actions.SetTask({
                                    task: resTask,
                                    actionType: action.update ? 'EDIT' : 'CREATE',
                                    redirect: true,
                                });
                            case 'TASK_INSTANCE':
                                this._store.dispatch(
                                    actions.AlertSuccess({
                                        message: action.update
                                            ? translate('Task Updated Successfully')
                                            : translate('Task Created Successfully'),
                                    })
                                );
                                return actions.SetTaskInstance({
                                    task: resTask,
                                    actionType: action.update ? 'EDIT' : 'CREATE',
                                    redirect: true,
                                });
                        }
                    }),
                    catchError(e => of(AlertError({ message: e })))
                );
            })
        )
    );

    createTaskLinks = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.CreateTaskLinks),
            debounceTime(100),
            mergeMap(action => {
                const linkRequests = action.links.map(link =>
                    this._api.tasks.createLink(action.taskId, {
                        app_name: link.app_name,
                        event_type: link.event_type,
                        expression: link.expression,
                        task_definition_id: link.task_definition_id,
                        custom_message: link.custom_message,
                        service_user_id: link.service_user_id,
                        organization: link.organization,
                    })
                );

                return forkJoin(linkRequests).pipe(
                    mergeMap(() =>
                        from([
                            actions.AlertSuccess({ message: 'All links created successfully.' }),
                            actions.GetLinks({ taskId: action.taskId }),
                        ])
                    ),
                    catchError(_err => of(actions.AlertError({ message: 'Some links could not be created.' })))
                );
            })
        );
    });

    createLink$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.CreateLink),
            debounceTime(100),
            mergeMap(action => {
                return this._api.tasks.createLink(action.taskId, action.linkData).pipe(
                    map((response: any) => {
                        this._store.dispatch(
                            AlertSuccess({
                                message: action.message || (action.linkData.id ? 'Link updated successfully' : 'Link created successfully'),
                            })
                        );
                        return actions.CreateLinkSuccess({ link: response });
                    }),
                    catchError(errorResponse => {
                        return of(AlertError({ message: errorResponse || 'Failed to create link' }));
                    })
                );
            })
        );
    });

    getLinks$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetLinks),
            mergeMap(action => {
                return this._api.tasks.getLinks(action.taskId).pipe(
                    map((links: any[]) => actions.GetLinksSuccess({ links })),
                    catchError(errorResponse => of(AlertError({ message: errorResponse?.message || 'Failed to get link' })))
                );
            })
        );
    });

    deleteLink$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.DeleteLink),
            delay(100),
            mergeMap(action =>
                this._api.tasks.deleteLink(action.taskId, action.linkId).pipe(
                    mergeMap((res: any) => {
                        const message = res?.response || translate('Link deleted successfully');
                        if (message && (message as string).toLowerCase().includes('cannot be deleted')) {
                            return of(AlertError({ message }));
                        }
                        return from([AlertSuccess({ message }), actions.DeleteLinkSuccess({ linkId: action.linkId })]);
                    }),
                    catchError(error => {
                        const message = error?.error?.response || error?.response || error || translate('Failed to delete link');
                        return of(AlertError({ message }));
                    })
                )
            )
        )
    );

    createTaskNotifiers = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.CreateTaskNotifiers),
            debounceTime(100),
            mergeMap(action => {
                const notifierRequests = action.notifiers.map(notifier =>
                    this._api.tasks.createNotifier(action.taskId, {
                        event_type: notifier.event_type,
                        schedule: notifier.schedule,
                        roles: notifier.roles,
                        users: notifier.users,
                        definition_id: notifier.definition_id,
                    })
                );

                return forkJoin(notifierRequests).pipe(
                    mergeMap(() =>
                        from([
                            actions.AlertSuccess({ message: 'All notifiers created successfully.' }),
                            actions.GetNotifiers({ taskId: action.taskId }),
                        ])
                    ),
                    catchError(_err => of(actions.AlertError({ message: 'Some notifiers could not be created.' })))
                );
            })
        );
    });

    createNotifier$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.CreateNotifier),
            debounceTime(100),
            mergeMap(action => {
                return this._api.tasks.createNotifier(action.taskId, action.notifierData).pipe(
                    map((response: any) => {
                        this._store.dispatch(
                            AlertSuccess({
                                message: 'Notifier created successfully',
                            })
                        );
                        return actions.CreateNotifierSuccess({ notifier: response });
                    }),
                    catchError(errorResponse => {
                        return of(AlertError({ message: errorResponse || 'Failed to create notifier' }));
                    })
                );
            })
        );
    });

    updateNotifier$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.UpdateNotifier),
            debounceTime(100),
            mergeMap(action => {
                return this._api.tasks.updateNotifier(action.taskId, action.notifierData).pipe(
                    map((response: any) => {
                        this._store.dispatch(
                            AlertSuccess({
                                message: 'Notifier updated successfully',
                            })
                        );
                        return actions.UpdateNotifierSuccess({ notifier: response });
                    }),
                    catchError(errorResponse => {
                        return of(AlertError({ message: errorResponse || 'Failed to update notifier' }));
                    })
                );
            })
        );
    });

    getNotifiers$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetNotifiers),
            mergeMap(action => {
                return this._api.tasks.getNotifiers(action.taskId).pipe(
                    map((notifiers: any[]) => actions.GetNotifiersSuccess({ notifiers })),
                    catchError(errorResponse => of(AlertError({ message: errorResponse?.message || 'Failed to get notifiers' })))
                );
            })
        );
    });

    deleteNotifier$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.DeleteNotifier),
            mergeMap(action =>
                this._api.tasks.deleteNotifier(action.taskId, action.eventType).pipe(
                    map((res: any) => {
                        this._store.dispatch(AlertSuccess({ message: res?.response || 'Notifier deleted successfully' }));
                        return actions.DeleteNotifierSuccess({ eventType: action.eventType });
                    }),
                    catchError(error => of(AlertError({ message: error?.message || 'Failed to delete notifier' })))
                )
            )
        )
    );

    private getTask = (tasks: Task[], taskId: string) => {
        let task = tasks?.find(task => task.id === taskId);
        if (task) {
            return task;
        } else {
            tasks?.forEach(childTask => {
                if (task) return;
                task = this.getTask(childTask.childTasks, taskId);
            });
        }
        return task;
    };

    updateTaskFromList = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.UpdateTaskFromList),
            debounceTime(100),
            withLatestFrom(this._store.select(getTasksOrganizationMap$)),
            mergeMap(([action, orgMap]) => {
                let task: Task = CommonUtilsService.cloneObject(this.getTask(orgMap?.tasks, action.taskId));
                if (!task) {
                    task = CommonUtilsService.cloneObject(this.getTask(orgMap?.taskInstances?.[action.taskDefinitionId], action.taskId));
                    delete task.childTasks;
                }
                if (!task) {
                    return of(
                        AlertError({ message: translate(action.taskType === 'TASK_DEFINITION' ? 'Plan not found' : 'Task not found') })
                    );
                }
                task[action.data.property] = action.data.value;
                return from(this._api.tasks.updateTaskFromList(task)).pipe(
                    map((response: Task) => {
                        switch (action.taskType) {
                            case 'TASK_DEFINITION':
                                return actions.SetTask({
                                    task: response,
                                    actionType: 'EDIT',
                                });
                            case 'TASK_INSTANCE':
                                return actions.SetTaskInstance({
                                    task: response,
                                    actionType: 'EDIT',
                                });
                        }
                    }),
                    catchError(errorResponse => {
                        return of(actions.AlertError({ message: errorResponse }));
                    })
                );
            })
        );
    });

    getTask$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetTask),
            debounceTime(100),
            withLatestFrom(
                this._store.select(getTaskBySelectedType$),
                this._store.select(getSelectedTaskType$),
                this._store.select(getCurrentOrganizationId$)
            ),
            mergeMap(([action, task, taskTypeFromStore, currentOrganizationId]) => {
                const taskType = action.taskType || taskTypeFromStore;
                if (task?.fields?.length > 0 || !action.taskId || this.fetchingTaskDetail[action.taskId]) {
                    return [];
                }
                this.fetchingTaskDetail[action.taskId] = true;
                return from(this._api.tasks.getTask({ taskId: action.taskId, taskType })).pipe(
                    map((response: Task) => {
                        this.fetchingTaskDetail[action.taskId] = false;
                        response.task_url = `${environment['encomply-ui']}/organizations/${currentOrganizationId}/tasks/${response?.id}?taskDefinitionId=${response?.parent}&taskType=${taskType}&rootDefinitionId=${response?.root_definition_id}`;
                        response.fields = response?.fields || [];
                        switch (taskType) {
                            case 'TASK_DEFINITION':
                                return actions.SetTask({
                                    task: response,
                                    actionType: 'EDIT',
                                });
                            case 'TASK_INSTANCE':
                                return actions.SetTaskInstance({
                                    task: response,
                                    actionType: 'EDIT',
                                });
                        }
                    }),
                    catchError(() => {
                        this.fetchingTaskDetail[action.taskId] = false;
                        return [];
                    })
                );
            })
        );
    });

    getTaskAssignees = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetTaskAssignees),
            debounceTime(100),
            withLatestFrom(this._store.select(getSelectedTaskType$)),
            mergeMap(([action, taskType]) => {
                if (action.loading) {
                    this._store.dispatch(actions.HideLoading());
                }
                if (this.fetchingTaskAssignees[action.taskId]) {
                    return [];
                }
                this.fetchingTaskAssignees[action.taskId] = true;
                return from(this._api.tasks.getTaskAssignees({ taskId: action.taskId, taskType })).pipe(
                    map((response: TaskAssignee[]) => {
                        this.fetchingTaskAssignees[action.taskId] = false;
                        return actions.SetTaskAssignee({
                            taskId: action.taskId,
                            assignees: response,
                            taskType,
                        });
                    }),
                    catchError(() => {
                        this.fetchingTaskAssignees[action.taskId] = false;
                        return [];
                    })
                );
            })
        );
    });

    getTasksStatusGroups = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetStatusGroups),
            debounceTime(100),
            mergeMap(() =>
                this._api.tasks.getStatusGroups().pipe(
                    map((response: any[]) => {
                        return actions.SetStatusGroups({ statusGroups: response });
                    }),
                    catchError(errorResponse => of(AlertError({ message: errorResponse })))
                )
            )
        );
    });

    createStatusGroup = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.CreateStatusGroup),
            debounceTime(100),
            mergeMap(action => {
                return this._api.tasks
                    .createStatusGroup({
                        id: action.id,
                        name: action.name,
                        description: action.description,
                        is_default: action.is_default,
                        statuses: action.statuses,
                    })
                    .pipe(
                        map((response: any) => {
                            this._store.dispatch(
                                AlertSuccess({
                                    message:
                                        response?.msg || action.id
                                            ? translate('Status Group updated successfully')
                                            : translate('Status Group created successfully'),
                                })
                            );
                            return actions.CreateStatusGroupSuccess({ statusGroup: response });
                        }),
                        catchError(errorResponse => {
                            return of(AlertError({ message: errorResponse || 'Failed to create status group' }));
                        })
                    );
            })
        );
    });

    getAssigneesRecursively = (taskId: string, taskInstances: Task[]) => {
        let assignees: TaskAssignee[];
        taskInstances.forEach(task => {
            if (assignees) {
                return;
            }
            if (task.id === taskId) {
                assignees = task.assignments;
                return;
            }
            if (task.childTasks?.length) {
                assignees = this.getAssigneesRecursively(taskId, task.childTasks);
            }
        });
        return assignees;
    };

    updateTaskAssignees = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.UpdateTaskAssignees),
            withLatestFrom(
                this._store.select(getTaskAssignees$),
                this._store.select(getRootChildTasks$),
                this._store.select(getSelectedTaskInstances$)
            ),
            mergeMap(([action, _assignees, taskDefinitions, taskInstances]) => {
                let assigneesToWork: TaskAssignee[];
                if (action.fromTable && action.taskId === action?.assignees?.[0]?.definition_id) {
                    assigneesToWork = this.getAssigneesRecursively(action.taskId, taskDefinitions);
                } else if (action.fromTable && action.taskId === action?.assignees?.[0]?.instance_id) {
                    assigneesToWork = this.getAssigneesRecursively(action.taskId, taskInstances);
                } else if (action?.taskId === _assignees?.[0]?.definition_id) {
                    assigneesToWork = _assignees;
                } else if (action?.taskId === action?.assignees?.[0]?.definition_id) {
                    assigneesToWork = action.assignees;
                }
                const deletedAssignees = assigneesToWork?.reduce((deletedAssignees, a) => {
                    const foundAssignee = action.assignees.find(b => a.permission === b.permission && a.principal === b.principal);
                    if (!foundAssignee) {
                        deletedAssignees.push(a.principal);
                    }
                    return deletedAssignees;
                }, [] as string[]);
                const changedAssignees = action.assignees.reduce((changed, a) => {
                    const foundAssignee = assigneesToWork?.find(b => a.permission === b.permission && a.principal === b.principal);
                    if (!foundAssignee) {
                        changed.push(a.principal);
                    }
                    return changed;
                }, [] as string[]);
                if (action.assignees?.find(assignee => assignee?.permission === 'admin') === undefined) {
                    this._store.dispatch(AlertError({ message: translate('At least one admin must be assigned') }));
                    return [];
                } else if (assigneesToWork) {
                    const unchangedAssignees = action.assignees.reduce((unchanged, a) => {
                        const foundAssignee = assigneesToWork?.find(b => a.permission === b.permission && a.principal === b.principal);
                        if (foundAssignee) {
                            unchanged.push(a);
                        }
                        return unchanged;
                    }, [] as TaskAssignee[]);
                    if (unchangedAssignees?.find(assignee => assignee?.permission === 'admin') === undefined) {
                        this._store.dispatch(AlertError({ message: translate('At least one admin must be unchanged') }));
                        return [];
                    }
                }
                deletedAssignees?.length > 0 &&
                    this._store.dispatch(
                        actions.DeleteTaskAssignee({
                            taskId: action.taskId,
                            assignees: deletedAssignees,
                            taskType: action.taskType,
                            message: action.message,
                            getAssignees: changedAssignees?.length === 0,
                        })
                    );
                if (changedAssignees?.length === 0) {
                    return action.message ? of(actions.AlertSuccess({ message: action.message })) : [];
                }
                const assignees = CommonUtilsService.cloneObject(action.assignees);
                assignees.forEach(assignee => {
                    delete assignee['new_assignee'];
                });
                this._store.dispatch(
                    actions.SubmitTaskAssignees({
                        assignees,
                        taskId: action.taskId,
                        message: action.message,
                        taskType: action.taskType,
                    })
                );
                return [];
            })
        );
    });

    deleteTaskAssignee = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.DeleteTaskAssignee),
            mergeMap(action => {
                return from(this._api.tasks.deleteTaskAssignee(action.taskId, action.assignees, action.taskType)).pipe(
                    mergeMap(() => {
                        const actionsToDispatch: Action[] = [];
                        if (action.getAssignees) {
                            actionsToDispatch.push(actions.GetTaskAssignees({ taskId: action.taskId, loading: true }));
                            action.message && actionsToDispatch.push(actions.AlertSuccess({ message: action.message }));
                        }
                        return actionsToDispatch;
                    }),
                    catchError(errorResponse => {
                        return of(AlertError({ message: errorResponse }));
                    })
                );
            })
        );
    });

    submitTaskAssignees = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.SubmitTaskAssignees),
            debounceTime(150),
            withLatestFrom(this._store.select(getTaskAssignees$)),
            mergeMap(([action, _assignees]) => {
                return from(this._api.tasks.updateTaskAssignees(action.taskId, action.taskType, action.assignees)).pipe(
                    map((_response: TaskAssignee[]) => {
                        action.message && this._store.dispatch(actions.AlertSuccess({ message: action.message }));
                        this._store.dispatch(actions.ShowLoading());
                        return actions.GetTaskAssignees({
                            taskId: action.taskId,
                            loading: true,
                        });
                    }),
                    catchError(errorResponse => {
                        return of(AlertError({ message: errorResponse }));
                    })
                );
            })
        );
    });

    getTasks = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetTasks),
            debounceTime(100),
            withLatestFrom(
                this._store.select(getAllUsersMap$),
                this._store.select(getCurrentOrganization$),
                this._store.select(getSessionState$),
                this._store.select(getSessionUserPermissions$),
                this._store.select(getTaskUserMetaprincipalIds$),
                this._store.select(getTasks$)
            ),
            mergeMap(([_action, usersMap, currentOrganization, sessionState, sessionUserPermissions, prinicipalIds, tasks]) => {
                if (!sessionState) {
                    return [];
                }
                if (tasks?.length > 1) {
                    return [];
                }
                const noUserPermissions = !usersMap && sessionUserPermissions;
                const noPrincipalIds = !prinicipalIds || prinicipalIds?.length === 0;
                if (noUserPermissions || noPrincipalIds) {
                    if (noUserPermissions) {
                        this._store.dispatch(actions.GetAllUsers());
                    }
                    if (noPrincipalIds) {
                        this._store.dispatch(actions.GetSessionUserCurrentOrganizationRoles());
                    }
                    setTimeout(() => {
                        this._store.dispatch(actions.GetTasks());
                    }, 500);
                    return [];
                }
                return from(this._api.tasks.getTasks()).pipe(
                    map((response: Task[]) => {
                        const tasks: Task[] = this.prepareTasksWithUserName(response, usersMap, currentOrganization);
                        return actions.SetTasks({ tasks });
                    }),
                    catchError(error => of(AlertError({ message: error?.msg || translate('Failed to get plans') })))
                );
            })
        );
    });

    prepareTasksWithUserName(tasks: Task[], usersMap: any, currentOrganization: Organization) {
        return tasks.map(task => {
            return {
                ...task,
                task_url: `${environment['encomply-ui']}/organizations/${currentOrganization.id}/tasks/${task.id}?taskDefinitionId=${
                    task.task_definition_id?.length > 0 ? task.task_definition_id : task.parent
                }&taskType=${task.task_definition_id?.length > 0 ? 'TASK_INSTANCE' : 'TASK_DEFINITION'}&rootDefinitionId=${
                    task.root_definition_id
                }`,
                created_by_name: usersMap[task.created_by]?.displayName,
                updated_by_name: usersMap[task.updated_by]?.displayName,
                organization_name: currentOrganization?.name,
                childTasks: [],
                showChild: false,
            };
        });
    }

    getChildTasks = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetChildTasks),
            debounceTime(100),
            withLatestFrom(
                this._store.select(getSelectedTaskType$),
                this._store.select(getAllUsersMap$),
                this._store.select(getCurrentOrganization$),
                this._store.select(getSessionUserPermissions$),
                this._store.select(getRootChildTasks$)
            ),
            mergeMap(([_action, selectedTaskType, usersMap, currentOrganization, sessionUserPermissions, childTasks]) => {
                if (childTasks?.length > 0) {
                    return [];
                }
                const taskType = _action.taskType || selectedTaskType;
                if (!usersMap && sessionUserPermissions) {
                    this._store.dispatch(actions.GetAllUsers());
                    setTimeout(() => {
                        this._store.dispatch(actions.GetChildTasks(_action));
                    }, 500);
                    return [];
                }
                return from(this._api.tasks.getChildTasks({ taskId: _action.taskId, taskType })).pipe(
                    map((response: Task[]) => {
                        const tasks = this.prepareTasksWithUserName(response, usersMap, currentOrganization);
                        const tasksMap = tasks.reduce((acc, childTask) => {
                            acc[childTask.id] = childTask;
                            return acc;
                        }, {} as { [property: string]: Task });
                        const instancesHierarychyMap = {};
                        tasks.forEach(childTask => {
                            this.transformInstancesToHierarchy(childTask, tasksMap, instancesHierarychyMap);
                        });
                        switch (taskType) {
                            case 'TASK_DEFINITION':
                                const instancesHierarychy = Object.entries(tasksMap).reduce((acc, [_, value]) => {
                                    if (value.parent === _action.taskId) {
                                        acc.push(value);
                                    }
                                    return acc;
                                }, [] as Task[]);
                                const membersList =
                                    instancesHierarychy?.length === 0 &&
                                    Object.keys(instancesHierarychyMap).reduce((acc, key) => {
                                        instancesHierarychyMap[key].parent !== '00000000-0000-0000-0000-000000000000' &&
                                            acc.push(instancesHierarychyMap[key]);
                                        return acc;
                                    }, [] as Task[]);
                                return actions.SetChildTasks({
                                    tasks: [...(instancesHierarychy || []), ...(membersList || [])],
                                    parentTaskId: _action.taskId,
                                });
                            case 'TASK_INSTANCE':
                                return actions.SetChildTaskInstances({ tasks, parentTaskId: _action.taskId });
                        }
                    }),
                    catchError(error =>
                        of(
                            AlertError({
                                message:
                                    error?.msg ||
                                    translate(
                                        _action.taskType === 'TASK_DEFINITION' ? 'Failed to get child plans' : 'Failed to get child tasks'
                                    ),
                            })
                        )
                    )
                );
            })
        );
    });

    getFutureTaskInstances = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetFutureTaskInstances),
            debounceTime(100),
            withLatestFrom(this._store.select(getSelectedTaskId$)),
            mergeMap(([_action, taskId]) => {
                if (!taskId) {
                    return of(AlertError({ message: translate('Select task to proceed') }));
                }
                return from(this._api.tasks.getFutureTaskInstances(taskId, _action.start, _action.end)).pipe(
                    map(
                        (
                            response: {
                                definition: Task;
                                schedule: string[];
                            }[]
                        ) =>
                            actions.SetFutureTaskInstances({
                                futureSchedules: response,
                            })
                    ),
                    catchError(error => of(AlertError({ message: error?.msg || translate('Failed to get future tasks') })))
                );
            })
        );
    });

    getTaskInstances = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetTaskInstances),
            debounceTime(100),
            withLatestFrom(
                this._store.select(getTaskDefinitionId$),
                this._store.select(getAllUsersMap$),
                this._store.select(getCurrentOrganization$),
                this._store.select(getSelectedTaskInstances$),
                this._store.select(getSessionUserPermissions$)
            ),
            mergeMap(([_action, taskDefId, usersMap, currentOrganization, taskInstances, sessionUserPermissions]) => {
                if (taskInstances?.length > 1) {
                    return [];
                }
                if (!usersMap && sessionUserPermissions) {
                    this._store.dispatch(actions.GetAllUsers());
                    setTimeout(() => {
                        this._store.dispatch(actions.GetTaskInstances());
                    }, 500);
                    return [];
                }
                return from(this._api.tasks.getTaskInstances(taskDefId)).pipe(
                    map((response: any[]) => {
                        const tasks: Task[] = this.prepareTasksWithUserName(
                            CommonUtilsService.cloneObject(response),
                            usersMap,
                            currentOrganization
                        );
                        const tasksMap = tasks.reduce((acc, childTask) => {
                            acc[childTask.id] = childTask;
                            return acc;
                        }, {} as { [property: string]: Task });
                        const instancesHierarychyMap = {};
                        tasks.forEach(childTask => {
                            this.transformInstancesToHierarchy(childTask, tasksMap, instancesHierarychyMap);
                        });
                        const instancesHierarychy = Object.entries(tasksMap).reduce((acc, [_, value]) => {
                            if (value.task_definition_id === taskDefId) {
                                acc.push(value);
                            }
                            return acc;
                        }, [] as Task[]);
                        return actions.SetTaskInstances({
                            taskInstances: [...(instancesHierarychy || [])],
                        });
                    }),
                    catchError(error => of(AlertError({ message: error?.msg || translate('Failed to get tasks') })))
                );
            })
        );
    });

    private transformInstancesToHierarchy(task: Task, tasksMap: { [key: string]: Task }, instancesHierarychyMap: { [key: string]: Task }) {
        if (tasksMap[task.parent]) {
            tasksMap[task.parent].childTasks.push(task);
        } else {
            instancesHierarychyMap[task.id] = task;
        }
    }

    getComments$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetComments),
            debounceTime(100),
            withLatestFrom(this._store.select(getComments$), this._store.select(getAllUsersMap$)),
            mergeMap(([action, comments, usersMap]) => {
                if (comments?.length > 0 && !action.update) {
                    return [];
                }
                return from(this._api.tasks.getComments({ taskId: action.taskId })).pipe(
                    map((response: Comment[]) => {
                        let comments = CommonUtilsService.cloneObject(response);
                        const grouped = comments.reduce((acc, comment) => {
                            if (!acc[comment.created_on]) {
                                acc[comment.created_on] = [];
                            }
                            acc[comment.created_on].push(comment);
                            return acc;
                        }, {});

                        let filteredComments = [];
                        Object.keys(grouped).forEach(key => {
                            const group = grouped[key];
                            const activeComments = group.filter(c => !c.is_deleted);
                            if (activeComments.length > 0) {
                                filteredComments = filteredComments.concat(activeComments);
                            } else {
                                const latestDeleted = group.reduce((latest, current) => {
                                    return new Date(current.updated_on) > new Date(latest.updated_on) ? current : latest;
                                });
                                filteredComments.push(latestDeleted);
                            }
                        });
                        comments = filteredComments;
                        comments?.forEach(comment => {
                            comment['updated_by_name'] = usersMap[comment.updated_by]?.displayName;
                        });
                        return actions.SetComments({
                            comments: comments,
                            taskId: action.taskId,
                        });
                    }),
                    catchError(errorResponse => {
                        return of(AlertError({ message: errorResponse?.msg || errorResponse || translate('Failed to get comments') }));
                    })
                );
            })
        );
    });

    getActionUpdates$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetActionUpdates),
            debounceTime(100),
            withLatestFrom(this._store.select(getActionUpdates$), this._store.select(getAllUsersMap$)),
            mergeMap(([action, actionUpdates, usersMap]) => {
                if (actionUpdates?.length > 0 && !action.update) {
                    return [];
                }
                return from(this._api.tasks.getActionUpdates({ taskId: action.taskId })).pipe(
                    map((response: ActionUpdate[]) => {
                        const updates = CommonUtilsService.cloneObject(response);
                        updates?.forEach(update => {
                            update['created_by_name'] = usersMap[update.created_by]?.displayName;
                        });
                        return actions.SetActionUpdates({
                            actionUpdates: updates,
                            taskId: action.taskId,
                        });
                    }),
                    catchError(errorResponse => {
                        return of(
                            AlertError({ message: errorResponse?.msg || errorResponse || translate('Failed to get action updates') })
                        );
                    })
                );
            })
        );
    });

    createComment$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.CreateComment),
            debounceTime(100),
            withLatestFrom(this._store.select(getAllUsersMap$)),
            mergeMap(([action, usersMap]) => {
                return from(this._api.tasks.createComment(action)).pipe(
                    map((response: Comment) => {
                        this._store.dispatch(AlertSuccess({ message: translate('Comment added successfully') }));
                        const comment = CommonUtilsService.cloneObject(response);
                        comment['updated_by_name'] = usersMap[comment.updated_by]?.displayName;
                        return actions.SetComment({
                            comment: comment,
                            taskId: action.taskId,
                        });
                    }),
                    catchError(errorResponse => {
                        return of(AlertError({ message: errorResponse?.msg || errorResponse || translate('Failed to create comment') }));
                    })
                );
            })
        );
    });

    updateComment$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.UpdateComment),
            debounceTime(100),
            withLatestFrom(this._store.select(getAllUsersMap$)),
            mergeMap(([action, usersMap]) => {
                return from(this._api.tasks.updateComment(action)).pipe(
                    mergeMap((response: Comment) => {
                        this._store.dispatch(AlertSuccess({ message: translate('Comment updated successfully') }));
                        const comment = CommonUtilsService.cloneObject(response);
                        comment['updated_by_name'] = usersMap[comment.updated_by]?.displayName;
                        return from([
                            actions.SetComment({
                                comment: comment,
                                taskId: action.taskId,
                            }),
                            actions.GetComments({ taskId: action.taskId, update: true }),
                        ]);
                    }),
                    catchError(errorResponse => {
                        return of(AlertError({ message: errorResponse?.msg || errorResponse || translate('Failed to update comment') }));
                    })
                );
            })
        );
    });

    getChildTaskInstancesWithDetails$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetChildTaskInstancesWithDetails),
            debounceTime(100),
            withLatestFrom(this._store.select(getAllUsersMap$), this._store.select(getCurrentOrganization$)),
            mergeMap(([action, usersMap, currentOrganization]) => {
                if (!action.taskId) {
                    return [];
                }
                return from(this._api.tasks.getChildTaskInstancesWithDetails(action.taskId)).pipe(
                    map((response: Task[]) => {
                        const tasks = this.prepareTasksWithUserName(response, usersMap, currentOrganization);
                        const tasksMap = tasks.reduce((acc, childTask) => {
                            acc[childTask.id] = childTask;
                            return acc;
                        }, {} as { [property: string]: Task });
                        const instancesHierarychyMap: { [property: string]: Task } = {};
                        tasks.forEach(childTask => {
                            this.transformInstancesToHierarchy(childTask, tasksMap, instancesHierarychyMap);
                        });
                        const instancesHierarychy = Object.entries(instancesHierarychyMap).reduce((acc, [_, value]) => {
                            if (value.root_instance_id === action.taskId) {
                                acc.push(value);
                            }
                            return acc;
                        }, [] as Task[]);
                        return actions.SetChildTaskInstances({
                            parentTaskId: action.taskId,
                            tasks: [...(instancesHierarychy || [])],
                        });
                    }),
                    catchError(errorResponse => {
                        return of(
                            AlertError({
                                message: errorResponse?.msg || errorResponse || translate('Failed to get child tasks with details'),
                            })
                        );
                    })
                );
            })
        );
    });

    searchChildTaskInstances$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.SearchChildTaskInstances),
            withLatestFrom(
                this._store.select(getAllUsersMap$),
                this._store.select(getCurrentOrganization$),
                this._store.select(getTasksOrganizationMap$)
            ),
            mergeMap(([action, usersMap, currentOrganization, orgMap]) => {
                return from(this._api.tasks.searchChildTaskInstancesWithDetails(action.rootInstanceId, action.filter)).pipe(
                    mergeMap((response: any) => {
                        const taskList: Task[] = Array.isArray(response)
                            ? response
                            : response?.response || response?.data || response?.tasks || [];
                        const preparedTasks = this.prepareTasksWithUserName(taskList, usersMap, currentOrganization);
                        const existingTaskInstances: Task[] = Object.values(
                            orgMap?.taskInstances || {}
                        ).flat() as Task[];
                        const getExistingFieldOptions = (taskId: string, fieldName: string): any[] | undefined => {
                            const existingTask = this.getTask(existingTaskInstances, taskId);
                            if (!existingTask) return undefined;
                            const existingField = existingTask.fields?.find((f: any) => f.name === fieldName);
                            return existingField?.options?.length > 0 ? existingField.options : undefined;
                        };
                        const tasks = preparedTasks.map(task => ({
                            ...task,
                            fields: task.fields?.map((field: any) => ({
                                ...field,
                                options:
                                    field.options?.length > 0
                                        ? field.options
                                        : getExistingFieldOptions(task.id, field.name) ?? field.options,
                            })),
                        }));
                        const taskIds = tasks.map(task => task.id);
                        // Store full task data in taskInstances and IDs in filteredChildTaskInstanceIds
                        return of(
                            actions.SetChildTaskInstances({ tasks, parentTaskId: action.rootInstanceId }),
                            actions.SetFilteredChildTaskInstances({ taskIds, rootInstanceId: action.rootInstanceId })
                        );
                    }),
                    catchError(errorResponse => {
                        if (errorResponse === null || errorResponse === undefined) {
                            return of(
                                actions.SetChildTaskInstances({ tasks: [], parentTaskId: action.rootInstanceId }),
                                actions.SetFilteredChildTaskInstances({ taskIds: [], rootInstanceId: action.rootInstanceId })
                            );
                        }
                        const message =
                            errorResponse?.msg ||
                            errorResponse?.message ||
                            errorResponse?.errorMessage ||
                            (typeof errorResponse === 'string' ? errorResponse : null) ||
                            translate('Failed to search tasks');
                        return of(AlertError({ message }));
                    })
                );
            })
        );
    });

    getChildTaskDefinitionsWithDetails$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetChildTaskDefinitionsWithDetails),
            debounceTime(100),
            withLatestFrom(this._store.select(getAllUsersMap$), this._store.select(getCurrentOrganization$)),
            mergeMap(([action, usersMap, currentOrganization]) => {
                if (!action.taskId) {
                    return [];
                }
                if (!usersMap || !currentOrganization) {
                    setTimeout(() => {
                        this._store.dispatch(actions.GetChildTaskDefinitionsWithDetails({ taskId: action.taskId }));
                    }, 100);
                    return [];
                }
                return from(this._api.tasks.getChildTaskDefintionsWithDetails(action.taskId)).pipe(
                    map((response: Task[]) => {
                        const tasks = this.prepareTasksWithUserName(
                            CommonUtilsService.cloneObject(response),
                            usersMap,
                            currentOrganization
                        );
                        const tasksMap = tasks.reduce((acc, childTask) => {
                            acc[childTask.id] = childTask;
                            return acc;
                        }, {} as { [property: string]: Task });
                        const instancesHierarychyMap = {};
                        tasks.forEach(childTask => {
                            this.transformInstancesToHierarchy(childTask, tasksMap, instancesHierarychyMap);
                        });
                        const instancesHierarychy = Object.entries(tasksMap).reduce((acc, [_, value]) => {
                            if (value.parent === action.taskId) {
                                acc.push(value);
                            }
                            return acc;
                        }, [] as Task[]);
                        const membersList =
                            instancesHierarychy?.length === 0 &&
                            Object.keys(instancesHierarychyMap).reduce((acc, key) => {
                                instancesHierarychyMap[key].parent !== '00000000-0000-0000-0000-000000000000' &&
                                    acc.push(instancesHierarychyMap[key]);
                                return acc;
                            }, [] as Task[]);
                        return actions.SetChildTasks({
                            tasks: [...(instancesHierarychy || []), ...(membersList || [])],
                            parentTaskId: action.taskId,
                        });
                    }),
                    catchError(errorResponse => {
                        return of(
                            AlertError({
                                message: errorResponse?.msg || errorResponse || translate('Failed to get child plans with details'),
                            })
                        );
                    })
                );
            })
        );
    });

    deleteOrDisableTask$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(actions.DeleteOrDisableTask),
                debounceTime(100),
                withLatestFrom(this._store.select(getTaskDefinitionId$)),
                mergeMap(([action, taskDefinitionId]) =>
                    from(this._api.tasks.deleteOrDisableTask({ task: action.task, enabled: action.enabled })).pipe(
                        map(response => {
                            this._store.dispatch(AlertSuccess({ message: (response as { response: string })?.response }));
                            setTimeout(() => {
                                if (action.childTask) {
                                    this._store.dispatch(actions.SetChildTasks({ tasks: [], parentTaskId: taskDefinitionId }));
                                    this._store.dispatch(actions.GetChildTasks({ taskId: taskDefinitionId, taskType: 'TASK_DEFINITION' }));
                                } else {
                                    this._store.dispatch(actions.SetTasks({ tasks: [] }));
                                    this._store.dispatch(actions.GetTasks());
                                }
                            }, 1000);
                        }),
                        catchError(errorResponse => {
                            this._store.dispatch(AlertError({ message: errorResponse as any }));
                            if (action.childTask) {
                                this._store.dispatch(actions.SetChildTasks({ tasks: [], parentTaskId: taskDefinitionId }));
                                this._store.dispatch(actions.GetChildTasks({ taskId: taskDefinitionId, taskType: 'TASK_DEFINITION' }));
                            } else {
                                this._store.dispatch(actions.SetTasks({ tasks: [] }));
                                this._store.dispatch(actions.GetTasks());
                            }
                            return [];
                        })
                    )
                )
            );
        },
        { dispatch: false }
    );

    getRootTaskInstances$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetRootTaskInstances),
            debounceTime(100),
            withLatestFrom(this._store.select(getCurrentOrganization$), this._store.select(getAllUsersMap$)),
            mergeMap(([action, currentOrganization, usersMap]) => {
                if (!action.taskDefinitionId) {
                    return [];
                }
                if (!usersMap || !currentOrganization) {
                    setTimeout(() => {
                        this._store.dispatch(actions.GetRootTaskInstances(action));
                    }, 100);
                    return [];
                }
                return from(this._api.tasks.getRootTaskInstances(action.taskDefinitionId)).pipe(
                    map((response: Task[]) => {
                        const tasks = this.prepareTasksWithUserName(
                            CommonUtilsService.cloneObject(response),
                            usersMap,
                            currentOrganization
                        );
                        return actions.SetTaskInstances({
                            taskInstances: tasks,
                        });
                    }),
                    catchError(errorResponse => {
                        return of(
                            AlertError({
                                message: errorResponse?.msg || errorResponse || translate('Failed to get child plans with details'),
                            })
                        );
                    })
                );
            })
        );
    });

    deleteComment$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.DeleteComment),
            debounceTime(100),
            mergeMap(action => {
                return from(this._api.tasks.deleteComment(action?.taskId, action?.commentId)).pipe(
                    mergeMap(response => {
                        return from([
                            AlertSuccess({
                                message: (response as { response: string })?.response || translate('Comment deleted successfully'),
                            }),
                            actions.GetComments({ taskId: action?.taskId, update: true }),
                        ]);
                    }),
                    catchError(errorResponse => {
                        return of(AlertError({ message: errorResponse?.msg || errorResponse || translate('Failed to delete comment') }));
                    })
                );
            })
        );
    });

    getTaskActions$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetTaskActions),
            debounceTime(100),
            withLatestFrom(this._store.select(getSelectedTaskId$), this._store.select(getTaskActions$)),
            mergeMap(([_action, taskDefinitionId, taskActions]) => {
                if (taskActions?.length > 0) {
                    return [];
                }
                return from(this._api.tasks.getTaskActions(taskDefinitionId)).pipe(
                    map(response => {
                        return actions.SetTaskActions({
                            actions: response,
                            taskDefinitionId,
                        });
                    }),
                    catchError(errorResponse => {
                        return of(
                            AlertError({
                                message: errorResponse?.msg || errorResponse || translate('Failed to get configurations'),
                            })
                        );
                    })
                );
            })
        );
    });

    saveTaskAction$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.SaveTaskAction),
            mergeMap(({ taskDefinitionId, actionData }) => {
                return from(this._api.tasks.saveTaskAction(taskDefinitionId, actionData)).pipe(
                    map(response => {
                        this._store.dispatch(AlertSuccess({ message: translate(`Configuration created successfully`) }));
                        return actions.SaveTaskActionSuccess({
                            action: response,
                            taskDefinitionId,
                        });
                    }),
                    catchError(errorResponse => {
                        return of(
                            actions.SaveTaskActionFailure({
                                error: errorResponse?.msg || errorResponse || translate('Failed to save configuration'),
                            }),
                            AlertError({
                                message: errorResponse?.msg || errorResponse || translate('Failed to save configuration'),
                            })
                        );
                    })
                );
            })
        );
    });

    updateTaskAction$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.UpdateTaskAction),
            mergeMap(({ taskDefinitionId, actionId, actionData }) => {
                return from(this._api.tasks.updateTaskAction(taskDefinitionId, actionId, actionData)).pipe(
                    map(response => {
                        this._store.dispatch(AlertSuccess({ message: translate(`Configuration updated successfully`) }));
                        return actions.UpdateTaskActionSuccess({
                            action: response,
                            taskDefinitionId,
                        });
                    }),
                    catchError(errorResponse => {
                        return of(
                            actions.UpdateTaskActionFailure({
                                error: errorResponse || translate('Failed to update configuration'),
                            }),
                            AlertError({
                                message: errorResponse || translate('Failed to update configuration'),
                            })
                        );
                    })
                );
            })
        );
    });

    deleteTaskAction$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.DeleteTaskAction),
            mergeMap(({ taskDefinitionId, actionId }) => {
                return from(this._api.tasks.deleteTaskAction(taskDefinitionId, actionId)).pipe(
                    map(() => {
                        this._store.dispatch(
                            AlertSuccess({
                                message: translate('Configuration deleted successfully'),
                            })
                        );
                        return actions.DeleteTaskActionSuccess({
                            actionId,
                            taskDefinitionId,
                        });
                    }),
                    catchError(errorResponse => {
                        return of(
                            actions.DeleteTaskActionFailure({
                                error: errorResponse?.msg || errorResponse || translate('Failed to delete configuration'),
                            }),
                            AlertError({
                                message: errorResponse?.msg || errorResponse || translate('Failed to delete configuration'),
                            })
                        );
                    })
                );
            })
        );
    });

    // Refresh task actions after successful CRUD operations
    refreshTaskActionsAfterSave$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.SaveTaskActionSuccess, actions.UpdateTaskActionSuccess, actions.DeleteTaskActionSuccess),
            map(() => {
                return actions.GetTaskActions();
            })
        );
    });
}
